import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import TotalRecordsCard from '../components/charts/TotalRecordsCard';
import GenderDistributionChart from '../components/charts/GenderDistributionChart';
import BirthsByYearChart from '../components/charts/BirthsByYearChart';
import BirthsByMonthChart from '../components/charts/BirthsByMonthChart';

// Configure axios for API calls
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:8000',
  withCredentials: true
});

const DashboardPage = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  
  const [dashboardData, setDashboardData] = useState({
    totalRecords: 0,
    genderStats: [],
    yearlyTrend: [],
    monthlyTrend: []
  });
  
  const [availableYears, setAvailableYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Redirect admin users to admin dashboard
  useEffect(() => {
    if (user?.role === 'admin') {
      navigate('/admin-dashboard');
    }
  }, [user, navigate]);

  useEffect(() => {
    if (!authLoading && user) {
      loadInitialData();
    }
  }, [authLoading, user]);

  useEffect(() => {
    if (!authLoading && user && availableYears.length > 0) {
      loadDashboardData();
    }
  }, [authLoading, user, availableYears, selectedYear]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!user) {
        return;
      }
      
      // Check if user has clinic assigned
      if (!user?.clinic_code) {
        setError('No clinic assigned to your account. Please contact administrator.');
        setLoading(false);
        return;
      }
      
      // Fetch available years from DOB
      const yearsResponse = await api.get('/api/dashboard/years');
      setAvailableYears(yearsResponse.data);
      
      setLoading(false);
    } catch (err) {
      console.error('Error loading initial data:', err);
      setError('Failed to load initial data');
      setLoading(false);
    }
  };

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Build params object with user's clinic auto-filter
      const params = {};
      if (selectedYear) {
        params.year = selectedYear;
      }
      
      // Auto-filter by user's clinic_code (VARCHAR clinic_id)
      if (user?.clinic_code) {
        params.clinic_id = user.clinic_code;
      }

      // Prepare yearly trend params (respects clinic filter but not year filter)
      const yearlyTrendParams = {};
      if (user?.clinic_code) {
        yearlyTrendParams.clinic_id = user.clinic_code;
      }

      // Prepare monthly params (includes year if selected)
      const monthParams = { ...params };

      // Fetch all dashboard metrics
      const [totalRes, genderRes, yearlyRes, monthlyRes] = await Promise.all([
        api.get('/api/dashboard/total-records', { params }),
        api.get('/api/dashboard/gender-stats', { params }),
        api.get('/api/dashboard/yearly-trend', { params: yearlyTrendParams }),
        api.get('/api/dashboard/monthly-trend', { params: monthParams })
      ]);

      // Show a single bar when a year is selected
      let processedYearlyData = yearlyRes.data || [];
      if (selectedYear) {
        processedYearlyData = processedYearlyData.filter(
          (item) => item.year === parseInt(selectedYear, 10)
        );
      }

      // Fill missing months with zero values for consistent charts
      const monthlySource = monthlyRes.data || [];
      const processedMonthlyData = Array.from({ length: 12 }, (_, index) => {
        const monthNumber = index + 1;
        const monthData = monthlySource.find((d) => d.month === monthNumber);
        return {
          month: monthNumber,
          count: monthData?.count || 0
        };
      });

      setDashboardData({
        totalRecords: totalRes.data.total,
        genderStats: genderRes.data,
        yearlyTrend: processedYearlyData,
        monthlyTrend: processedMonthlyData
      });

      setLoading(false);
    } catch (err) {
      console.error('Error loading dashboard data:', err);
      setError('Failed to load dashboard data. Please try again.');
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setSelectedYear('');
  };

  if (authLoading || (loading && availableYears.length === 0)) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 font-medium">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error && !loading && !authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md">
          <p className="text-red-800 font-medium">{error}</p>
          <button 
            onClick={loadDashboardData}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-4">
          <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-600 mt-1">
            Welcome, <span className="font-semibold">{user?.full_name || user?.username}</span>
            {user?.clinic_name && (
              <span className="ml-2 text-blue-600">({user.clinic_name})</span>
            )}
          </p>
        </div>

        {/* Filters Section */}
        <div className="bg-white rounded-lg shadow-md p-5 mb-5">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Filters</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Year Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Filter by Year
              </label>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Years</option>
                {availableYears.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>

            {/* Clear Filters Button */}
            <div className="flex items-end">
              <button
                onClick={clearFilters}
                className="w-full px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors"
              >
                Clear Filters
              </button>
            </div>
          </div>
        </div>

        {/* Total Records Card */}
        <TotalRecordsCard 
          totalRecords={dashboardData.totalRecords}
          selectedYear={selectedYear}
          clinicName={user?.clinic_name}
        />

        {/* Charts Grid - Top Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-5">
          <GenderDistributionChart data={dashboardData.genderStats} />
          <BirthsByYearChart 
            data={dashboardData.yearlyTrend}
            selectedYear={selectedYear}
            clinicName={user?.clinic_name}
          />
        </div>

        {/* Monthly Trend Chart - Full Width */}
        <BirthsByMonthChart 
          data={dashboardData.monthlyTrend}
          selectedYear={selectedYear}
          clinicName={user?.clinic_name}
        />
      </div>
    </div>
  );
};

export default DashboardPage;
